import { Input, InputNumber, Form as AntdForm } from "antd"
import { ErrorAlert } from "components/atoms/Alert"
import { Button } from "components/atoms/Button"
import { Spin } from "components/atoms/Spin"
import { Form, FormItem, FormItemCenter } from "components/atoms/form"
import { FormTitle } from "components/atoms/form/FormTitle"
import { NotFound } from "components/molecules/404"
import {
  useFetchCompany,
  useInitializeAccountData,
} from "hooks/useInitializeAccountData"
import { useAsyncLoading } from "hooks/useLoading"
import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useRecoilValue } from "recoil"
import { companyState } from "recoils/companyState"
import { isClientNotFoundState } from "recoils/isNotFoundState"
import styled from "styled-components"
import { upsertCompanyDetail } from "usecases/firebase/functions"

import type { CompanyUpsert } from "interfaces/CompanyIF"

const StyledButton = styled(Button)`
  width: 12rem;
`

export const CompanyEditPage: React.VFC = () => {
  const navigate = useNavigate()
  const [error, setError] = useState("")
  const [loading, asyncLoading] = useAsyncLoading("companyEdit")
  const company = useRecoilValue(companyState)

  const isClientNotFound = useRecoilValue(isClientNotFoundState)
  const [form] = AntdForm.useForm()
  const fetchCompany = useFetchCompany()
  const initialize = useInitializeAccountData()
  useEffect(() => {
    asyncLoading(
      initialize()
        .then(() => {
          form.resetFields()
        })
        .catch((e) => {
          setError(e.message)
        }),
    )
    // MEMO: 初回描画のみ実行する
  }, [])

  const onFinish = (values: unknown) => {
    asyncLoading(
      upsertCompanyDetail({
        name: (values as CompanyUpsert).name,
        email: (values as CompanyUpsert).email,
        telephoneNumber: (values as CompanyUpsert).telephoneNumber,
        representativeName: (values as CompanyUpsert).representativeName,
        staffName: (values as CompanyUpsert).staffName,
        employeesNumber: (values as CompanyUpsert).employeesNumber,
        overview: (values as CompanyUpsert).overview,
      })
        .then(async () => {
          await fetchCompany()
          navigate("/company")
        })
        .catch((e) => {
          setError(e.message)
        }),
    )
  }

  const onFinishFailed = (errorInfo: any) => {
    console.log("Failed:", errorInfo)
  }

  if (isClientNotFound) {
    return <NotFound />
  }
  return loading ? (
    <Spin />
  ) : (
    <Form
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={company}
      layout="vertical"
      requiredMark={false}
      form={form}
    >
      <FormTitle>会社情報</FormTitle>

      <FormItem
        label="会社名・屋号"
        name="name"
        required
        rules={[{ required: true, message: "会社名・屋号を入力してください" }]}
      >
        <Input placeholder="株式会社船員求人広告" maxLength={100} />
      </FormItem>

      <FormItem
        label="代表者名"
        name="representativeName"
        required
        rules={[{ required: true, message: "代表者名を入力してください" }]}
      >
        <Input placeholder="船員 太郎" maxLength={100} />
      </FormItem>

      <FormItem
        label="担当者名"
        name="staffName"
        required
        rules={[{ required: true, message: "担当者名を入力してください" }]}
      >
        <Input placeholder="広告 次郎" maxLength={100} />
      </FormItem>

      <FormItem
        label="従業員数"
        name="employeesNumber"
        required
        rules={[
          {
            required: true,
            message: "従業員の人数を数字で入力してください",
          },
        ]}
      >
        <InputNumber placeholder="3" addonAfter="人" maxLength={100} min={0} />
      </FormItem>

      <FormItem
        label="電話番号"
        name="telephoneNumber"
        required
        rules={[{ required: true, message: "電話番号を入力してください" }]}
      >
        <Input type="tel" placeholder="090-0000-0000" maxLength={100} />
      </FormItem>

      <FormItem
        label="メールアドレス"
        name="email"
        required
        rules={[
          {
            type: "email",
            message: "メールアドレスを入力してください",
          },
          { required: true, message: "メールアドレスを入力してください" },
        ]}
      >
        <Input type="email" placeholder="recruit@example.com" maxLength={100} />
      </FormItem>

      <FormItem
        label="会社概要"
        name="overview"
        required
        rules={[{ required: true, message: "会社概要を入力してください" }]}
      >
        <Input.TextArea
          rows={4}
          placeholder="会社の概要情報を入力してください。"
          maxLength={500}
        />
      </FormItem>
      {error && <ErrorAlert>{error}</ErrorAlert>}
      <FormItemCenter>
        <StyledButton
          isLoading={loading}
          className={"primary"}
          htmlType="submit"
        >
          保存
        </StyledButton>
      </FormItemCenter>
    </Form>
  )
}
